// Colourway × size matrix model [MB D10, D22; 02 §18; BR-UI-002]. Columns come from the style's size grid,
// never hard-coded. Cells are non-negative integer garment counts. Nothing pasted is dropped silently.

export interface MatrixRow {
  readonly colourway: string;
  readonly cells: Readonly<Record<string, number | null>>;
}

export interface CellUpdate {
  readonly colourway: string;
  readonly size: string;
  readonly value: number;
}

export interface Rejected {
  readonly where: string;
  readonly value: string;
  readonly reasonKey: string;
}

export interface PasteResult {
  readonly updates: readonly CellUpdate[];
  readonly rejected: readonly Rejected[];
  /** Number of pasted cells checked (every cell, accepted or not). */
  readonly validated: number;
}

export const MATRIX_REASON = Object.freeze({
  notInteger: 'ui.matrix.notInteger',
  negative: 'ui.matrix.negative',
  unknownSize: 'ui.matrix.unknownSize',
  unknownColour: 'ui.matrix.unknownColour',
  outOfGrid: 'ui.matrix.outOfGrid',
});

/** Parses one cell: '' → null (clear); '1 200' / '1,200' accepted as 1200; anything else rejected. */
export function parseCell(
  raw: string,
): { ok: true; value: number | null } | { ok: false; reasonKey: string } {
  const s = raw.trim().replace(/[\s\u00a0\u202f,]/g, '');
  if (s === '') return { ok: true, value: null };
  if (!/^-?\d+$/.test(s)) return { ok: false, reasonKey: MATRIX_REASON.notInteger };
  const n = Number(s);
  if (n < 0) return { ok: false, reasonKey: MATRIX_REASON.negative };
  return { ok: true, value: n };
}

export interface PasteTarget {
  readonly sizes: readonly string[];
  readonly colourways: readonly string[];
  /** Anchor cell (top-left of the paste) when the block has no headers. */
  readonly startRow: number;
  readonly startCol: number;
}

/**
 * Excel/TSV paste. If the first line names sizes of the grid it is a header (columns mapped by name);
 * if the first column names colourways, rows are mapped by name. Unknown headers / colours are rejected
 * as a whole with their values listed; cells outside the grid are rejected, not clipped.
 */
export function parsePaste(tsv: string, target: PasteTarget): PasteResult {
  const lines = tsv
    .replace(/\r\n?/g, '\n')
    .replace(/\n$/, '')
    .split('\n')
    .map((l) => l.split('\t'));
  const sizeSet = new Set(target.sizes);
  const colourSet = new Set(target.colourways);
  const first = lines[0] ?? [];
  const firstCellsLookLikeSizes = first
    .filter((c) => c.trim() !== '')
    .every((c) => !/^\s*-?[\d\s,]+$/.test(c));
  const hasHeader =
    lines.length > 1 && firstCellsLookLikeSizes && first.some((c) => sizeSet.has(c.trim()));
  const body = hasHeader ? lines.slice(1) : lines;
  const hasRowLabels =
    body.length > 0 &&
    body.every((r) => colourSet.has((r[0] ?? '').trim()) || /[A-Za-z]/.test(r[0] ?? ''));
  const labelOffset = hasRowLabels ? 1 : 0;
  const header = hasHeader ? first.slice(labelOffset).map((c) => c.trim()) : null;
  const updates: CellUpdate[] = [];
  const rejected: Rejected[] = [];
  let validated = 0;

  body.forEach((cells, r) => {
    const label = hasRowLabels ? (cells[0] ?? '').trim() : null;
    const colourway = label ?? target.colourways[target.startRow + r];
    const values = cells.slice(labelOffset);
    values.forEach((raw, c) => {
      validated += 1;
      const size = header ? header[c] : target.sizes[target.startCol + c];
      const where = `${colourway ?? `row ${target.startRow + r + 1}`} / ${size ?? `col ${target.startCol + c + 1}`}`;
      if (colourway === undefined || size === undefined) {
        rejected.push({ where, value: raw, reasonKey: MATRIX_REASON.outOfGrid });
        return;
      }
      if (!colourSet.has(colourway)) {
        rejected.push({ where, value: raw, reasonKey: MATRIX_REASON.unknownColour });
        return;
      }
      if (!sizeSet.has(size)) {
        rejected.push({ where, value: raw, reasonKey: MATRIX_REASON.unknownSize });
        return;
      }
      const parsed = parseCell(raw);
      if (!parsed.ok) rejected.push({ where, value: raw, reasonKey: parsed.reasonKey });
      else updates.push({ colourway, size, value: parsed.value ?? 0 });
    });
  });
  return { updates, rejected, validated };
}

export function applyUpdates(
  rows: readonly MatrixRow[],
  updates: readonly CellUpdate[],
): MatrixRow[] {
  const byColour = new Map<string, Record<string, number | null>>();
  for (const u of updates) {
    const m = byColour.get(u.colourway) ?? {};
    m[u.size] = u.value;
    byColour.set(u.colourway, m);
  }
  return rows.map((row) => {
    const patch = byColour.get(row.colourway);
    return patch ? { colourway: row.colourway, cells: { ...row.cells, ...patch } } : row;
  });
}

export interface MatrixTotals {
  readonly byRow: Readonly<Record<string, number>>;
  readonly bySize: Readonly<Record<string, number>>;
  readonly grand: number;
}

export function totals(rows: readonly MatrixRow[], sizes: readonly string[]): MatrixTotals {
  const byRow: Record<string, number> = {};
  const bySize: Record<string, number> = Object.fromEntries(sizes.map((s) => [s, 0]));
  let grand = 0;
  for (const row of rows) {
    let sum = 0;
    for (const s of sizes) {
      const v = row.cells[s] ?? 0;
      sum += v;
      bySize[s] = (bySize[s] ?? 0) + v;
    }
    byRow[row.colourway] = sum;
    grand += sum;
  }
  return { byRow, bySize, grand };
}

/** Save-time validation: a cell keyed by a size outside the grid, or an invalid value, blocks save. */
export function validateMatrix(rows: readonly MatrixRow[], sizes: readonly string[]): Rejected[] {
  const sizeSet = new Set(sizes);
  const out: Rejected[] = [];
  for (const row of rows)
    for (const [size, v] of Object.entries(row.cells)) {
      const where = `${row.colourway} / ${size}`;
      if (!sizeSet.has(size))
        out.push({ where, value: String(v ?? ''), reasonKey: MATRIX_REASON.unknownSize });
      else if (v !== null && (!Number.isInteger(v) || v < 0))
        out.push({
          where,
          value: String(v),
          reasonKey: v < 0 ? MATRIX_REASON.negative : MATRIX_REASON.notInteger,
        });
    }
  return out;
}
